import { useForm } from 'react-hook-form';
import gYupResolver from '../../technical/form/gYupResolver.ts';
import * as yup from 'yup';
import { Stack } from '@mui/joy';
import { FormPredefinedDateRangeInput } from '../../technical/form/FormPredefinedDateRangeInput.tsx';
import type { PredefinedRangeLabel } from '../../predefinedDateRanges.ts';
import { defaultRowSpacing } from '../../StackSpacing.ts';
import { updateWidgetState, useWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import { calculateDefaultPresetRange } from 'components/management/TilesDateRangeSelector.tsx';
import WidgetSettings from 'components/management/customDashboard/WidgetSettings.tsx';
import { UserSettingsDocument, useUpdateUserSettingsMutation } from 'generated/graphql.tsx';
import { FormPortfolioFilters } from 'components/technical/form/FormPortfolioFilters.tsx';
import {
  portfolioFiltersSchema,
  type PortfolioFilters,
} from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';

const schema = yup
  .object({
    title: yup.string().required(),
    predefinedDateRange: yup.object().required(),
  })
  .concat(portfolioFiltersSchema);

interface FormState extends Partial<PortfolioFilters> {
  title: string;
  predefinedDateRange: {
    label: PredefinedRangeLabel;
  };
}

export type TradingActivityWidgetState = {
  title: string;
  predefinedDateRange: PredefinedRangeLabel;
} & Partial<PortfolioFilters>;

export const TradingActivityWidgetSettings = ({
  id,
  onFinishedEditing,
  onRemovedWidget,
  presetKey,
}: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
  presetKey: string;
}) => {
  const initialState = useWidgetState<TradingActivityWidgetState>(id);
  const updater = updateWidgetState<TradingActivityWidgetState>();
  const [updateUserSettings] = useUpdateUserSettingsMutation();

  const methods = useForm<FormState>({
    resolver: gYupResolver(schema),
    defaultValues: {
      ...initialState,
      predefinedDateRange: calculateDefaultPresetRange(initialState.predefinedDateRange),
    },
  });

  const handleFormSubmit = async (input: FormState): Promise<void> => {
    await updater({
      id,
      state: {
        ...input,
        title: input.title,
        predefinedDateRange: input.predefinedDateRange.label,
      },
      updateBackend: true,
    });

    onFinishedEditing();
  };

  return (
    <WidgetSettings
      methods={methods}
      handleFormSubmit={handleFormSubmit}
      onRemovedWidget={async () => {
        await updateUserSettings({
          variables: {
            settings: {
              [presetKey]: null,
            },
          },
          refetchQueries: [UserSettingsDocument],
        });

        await onRemovedWidget();
      }}
    >
      <Stack width={'100%'} rowGap={defaultRowSpacing}>
        <FormPredefinedDateRangeInput name={'predefinedDateRange'} label={'Date range'} />
        <FormPortfolioFilters />
      </Stack>
    </WidgetSettings>
  );
};
